import { addDataLayerEntry } from '@printdeal/data-layer-manager';

declare global {
  interface Window {
    YT?: typeof YT;
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface PlayerInfo {
  videoData: {
    title: string;
    video_id: string;
  }
}

interface Player extends YT.Player {
  playerInfo: PlayerInfo;
}

export const loadYouTubeAPI = () => {
  if (!window.YT) {
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.body.appendChild(script);
  }
  // Cleanup function to remove the API script
  return () => {
    const script = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (script) {
      document.body.removeChild(script);
    }
  };
};

const onPlayerReady = (event: YT.PlayerEvent, autoPlayFlag: number) => {
  if (autoPlayFlag === 1) {
    event.target.playVideo();
  }
};

const onPlayerStateChange = (event: YT.OnStateChangeEvent, assetId: string) => {
  const { data, target } = event;

  const { playerInfo } = target as Player;
  const { videoData } = playerInfo;
  const payload = {
    id: assetId,
    title: videoData.title,
    url: target.getVideoUrl(),
    videoId: videoData.video_id,
  };

  const handlers: Partial<Record<YT.PlayerState, () => void>> = {
    [YT.PlayerState.PLAYING]: () => {
      addDataLayerEntry('video playing', payload);
    },
    [YT.PlayerState.PAUSED]: () => {
      addDataLayerEntry('video paused', payload);
    },
    [YT.PlayerState.ENDED]: () => {
      addDataLayerEntry('video ended', payload);
    },
    [YT.PlayerState.BUFFERING]: () => {
      addDataLayerEntry('video buffering', payload);
    },
  };

  // Execute the handler for the current state, if it exists
  const handler = handlers[data];
  if (handler) {
    handler();
  } else {
    // eslint-disable-next-line no-console
    console.error('Unhandled player state:', data);
  }
};

export const initializePlayer = (assetId: string, videoId: string, autoPlayFlag: number) =>
  new window.YT.Player(`video-player-${assetId}`, {
    videoId,
    width: 800,
    height: 450,
    playerVars: {
      autoplay: autoPlayFlag,
      enablejsapi: 1,
    },
    events: {
      onReady: (event: YT.PlayerEvent) => onPlayerReady(event, autoPlayFlag),
      onStateChange: (event: YT.OnStateChangeEvent) => onPlayerStateChange(event, assetId),
    },
  });
